import React, {FC, useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {useLocalizer} from '@/common/services/localizer';
import {copyToClipboard} from '@/common/helpers/utils/copyToClipboard';
import {LocationPoint} from '@/common/interfaces/location';
import {RouteName} from '@/app/constants/routing';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {selectDomainConfig} from '@/app/store/config/selectors';
import styles from './WeatherShareMenu.css';

interface WeatherShareMenuProps {
	location: LocationPoint;
	date: string;
	onClose(): void;
}

export const WeatherShareMenu: FC<WeatherShareMenuProps> = props => {
	const {location, date, onClose} = props;

	const [copied, setCopied] = useState(false);
	useEffect(() => {
		if (!copied) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setCopied(false);
		}, 3000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [copied]);

	const handleCopy = () => {
		copyToClipboard(cleanUrl);
		setCopied(true);
	};

	const {domainName} = useSelector(selectDomainConfig)!;
	const urlBuilder = useUrlBuilder();
	const t = useLocalizer();

	const cleanUrl = `https://${domainName}${urlBuilder(RouteName.weather, {location, dayDate: date})}`;
	const encodedUrl = encodeURIComponent(cleanUrl);
	const title = t('sharedContentTitle', {domainName});

	return (
		<div className={styles.root}>
			<button className={styles.closeButton} type='button' onClick={onClose} />
			<p>{t('shareTitle')}</p>
			{[
				{
					name: 'facebook',
					link: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
				},
				{
					name: 'telegram',
					link: `https://telegram.me/share/url?url=${encodedUrl}&text=${title}`,
				},
				{
					name: 'viber',
					link: `viber://forward?text=${title} ${encodedUrl}`,
				},
				{
					name: 'twitter',
					link: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${title}`,
				},
			].map(({name, link}) => (
				<a
					key={name}
					className={cn(styles.shareItem, styles[name])}
					target='_blank'
					href={link}
					rel='noreferrer'
				/>
			))}

			<p>{t('copySharedLink')}</p>
			<div className={styles.linkWrapper}>
				<button className={styles.copyButton} type='button' onClick={handleCopy} />
				{copied && <span className={styles.copiedInfo}>{t('sharedLinkCopied')}</span>}
				<p className={styles.linkText}> {cleanUrl}</p>
			</div>
		</div>
	);
};

WeatherShareMenu.displayName = 'WeatherShareMenu';
